/**
 * Ministry archetypes from the staff/volunteer assessment: one source for display
 * order, labels, descriptions and colors. Palette follows the staff Assessment
 * dashboard (sage/sky/slate). Long-form copy and icons stay in `data/archetypeDescriptions`.
 *
 * Each entry:
 * - `key`         lowercase id used by the API
 * - `label`       full name ("Visionary Leader")
 * - `shortLabel`  one word for compact badges ("Visionary")
 * - `description` one-line summary
 * - `chartColor`  hex for ApexCharts series
 * - `badgeClass`  Tailwind classes for a pill/badge background, text and border (light + dark)
 */

/** Canonical display order. */
export const ARCHETYPE_KEYS = ["visionary", "shepherd", "organizer", "evangelist", "teacher", "servant"];

export const ARCHETYPES = {
  visionary: {
    key: "visionary",
    label: "Visionary Leader",
    shortLabel: "Visionary",
    description: "Big-picture thinker who inspires with bold ideas",
    chartColor: "#91B9A8",
    badgeClass:
      "bg-brand-100 text-brand-800 border border-brand-500/60 dark:bg-brand-900/40 dark:text-brand-200",
  },
  shepherd: {
    key: "shepherd",
    label: "Shepherding Pastor",
    shortLabel: "Shepherd",
    description: "Pastoral, empathetic, people-focused caregiver",
    chartColor: "#6D9B8A",
    badgeClass:
      "bg-brand-200 text-brand-900 border border-brand-600/60 dark:bg-brand-800/50 dark:text-brand-100",
  },
  organizer: {
    key: "organizer",
    label: "Organizational Administrator",
    shortLabel: "Organizer",
    description: "Detail-oriented, process-driven planner",
    chartColor: "#7BAFD4",
    badgeClass:
      "bg-sky-50 text-sky-800 border border-sky-300/60 dark:bg-sky-900/30 dark:text-sky-200",
  },
  evangelist: {
    key: "evangelist",
    label: "Outreach Evangelist",
    shortLabel: "Evangelist",
    description: "Outgoing, enthusiastic connector and recruiter",
    chartColor: "#A3C9B8",
    badgeClass:
      "bg-brand-50 text-brand-700 border border-brand-400/60 dark:bg-brand-900/30 dark:text-brand-300",
  },
  teacher: {
    key: "teacher",
    label: "Teaching Mentor",
    shortLabel: "Teacher",
    description: "Knowledge-focused educator and content creator",
    chartColor: "#8FA8C4",
    badgeClass:
      "bg-slate-100 text-slate-700 border border-slate-400/60 dark:bg-slate-800/60 dark:text-slate-200",
  },
  servant: {
    key: "servant",
    label: "Helpful Servant",
    shortLabel: "Servant",
    description: "Behind-the-scenes, humble, task-oriented helper",
    chartColor: "#B0BEC5",
    badgeClass:
      "bg-gray-100 text-gray-700 border border-gray-400/60 dark:bg-gray-800/60 dark:text-gray-200",
  },
};

/** Chart series colors in `ARCHETYPE_KEYS` order. */
export const ARCHETYPE_CHART_COLORS = ARCHETYPE_KEYS.map((key) => ARCHETYPES[key].chartColor);

/** Looks up an archetype by key (case-insensitive); null when unknown or blank. */
export const getArchetype = (key) =>
  (typeof key === "string" && ARCHETYPES[key.trim().toLowerCase()]) || null;
